import React from "react";
import { Helmet } from "react-helmet-async";

const SITE = "https://riznwash.com";

/**
 * Reusable head-tag helper.
 * - `title` / `description` : per-page overrides
 * - `path` : canonical path (must start with '/')
 * - `noindex` : true for auth pages, admin gates, etc.
 * - `jsonLd` : one JSON-LD object OR an array of them (BreadcrumbList, FAQPage, LocalBusiness…)
 * - `image` : absolute or path to the OG/Twitter share image
 */
export default function Seo({
  title,
  titleOverride,
  description,
  path = "/",
  noindex = false,
  jsonLd = null,
  image = null,
}) {
  const url = `${SITE}${path}`;
  const fullTitle = titleOverride
    ? titleOverride
    : title
    ? `${title} · RIZ N WASH Gurugram`
    : "Doorstep Laundry & Dry Cleaning in Gurugram | RIZ N WASH";
  const desc =
    description ||
    "RIZ N WASH is a doorstep laundry & dry cleaning service in Gurugram, Delhi NCR. Wash & fold, ironing, dry cleaning and shoe/bag care with free pickup and 48-hour turnaround.";
  const ogImage = image
    ? image.startsWith("http") ? image : `${SITE}${image}`
    : `${SITE}/logos/brand-logo.png`;

  const jsonLdList = Array.isArray(jsonLd) ? jsonLd : jsonLd ? [jsonLd] : [];

  return (
    <Helmet prioritizeSeoTags>
      <title>{fullTitle}</title>
      <meta name="description" content={desc} />
      <link rel="canonical" href={url} />
      {noindex && <meta name="robots" content="noindex,nofollow" />}
      {/* Open Graph */}
      <meta property="og:type" content="website" />
      <meta property="og:site_name" content="RIZ N WASH" />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={desc} />
      <meta property="og:url" content={url} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:locale" content="en_IN" />
      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={desc} />
      <meta name="twitter:image" content={ogImage} />
      {/* JSON-LD (BreadcrumbList, LocalBusiness, FAQPage, etc.) */}
      {jsonLdList.map((obj, i) => (
        <script key={i} type="application/ld+json">
          {JSON.stringify(obj)}
        </script>
      ))}
    </Helmet>
  );
}
